import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { X, Brain, Users, TrendingUp } from "lucide-react";
import OrganicShape from "./OrganicShape";
import { fetchChemistryNotifications } from "../../services/businessDashApi";

export default function BoardDynamicsNotification({
  isOpen,
  onClose,
  onStartAssessment,
}) {
  const [notificationData, setNotificationData] = useState(null);
  const [showNotification, setShowNotification] = useState(false);

  // Check with backend API if assessment is due
  useEffect(() => {
    const checkNotification = async () => {
      const boardMemberId = localStorage.getItem("board_member_id");

      if (!boardMemberId) {
        console.warn("No board member ID found in localStorage");
        return;
      }

      try {
        const notification = await fetchChemistryNotifications(boardMemberId);

        if (notification.has_pending) {
          setShowNotification(true);
          setNotificationData(notification);
        }
      } catch (error) {
        console.error("Failed to check chemistry notifications:", error);
      }
    };

    checkNotification();
  }, []);

  // Keep old local check as fallback
  const shouldShowQuarterly = () => {
    const lastShown = localStorage.getItem("dynamicsNotificationLastShown");
    if (!lastShown) return true;

    const lastShownDate = new Date(lastShown);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return lastShownDate < threeMonthsAgo;
  };

  const handleClose = () => {
    localStorage.setItem(
      "dynamicsNotificationLastShown",
      new Date().toISOString()
    );
    onClose();
  };

  // Use backend notification data if available, otherwise fall back to local check
  if (!showNotification && !shouldShowQuarterly()) {
    return null;
  }

  return (
    <Dialog open={isOpen || showNotification} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Brain className="w-6 h-6 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold">
              Optimize Your Board Dynamics
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="text-center">
            <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <Users className="w-10 h-10 text-primary" />
            </div>
            <p className="text-foreground/80 text-sm leading-relaxed">
              {notificationData?.message ||
                "Discover how your board can improve decision-making efficiency with our tailored assessment tool for sustainability-focused organizations."}
            </p>
            {notificationData?.due_date && (
              <p className="text-xs text-muted-foreground mt-2">
                Due: {notificationData.due_date}
              </p>
            )}
          </div>

          <OrganicShape className="p-4 bg-gradient-subtle">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-primary" />
                <div>
                  <div className="font-medium text-sm">Quick Assessment</div>
                  <div className="text-xs text-foreground/60">
                    8 questions • 5 minutes
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-primary font-semibold text-sm">
                  Smart Defaults
                </div>
                <div className="text-xs text-foreground/60">
                  Minimize decisions
                </div>
              </div>
            </div>
          </OrganicShape>

          <div className="space-y-3">
            <Button
              onClick={() => {
                onStartAssessment();
                handleClose();
              }}
              className="w-full hover:scale-105 transition-transform"
              size="lg"
            >
              Start 5-Minute Setup
            </Button>
            <Button
              variant="ghost"
              onClick={handleClose}
              className="w-full text-sm"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
